import LinkIcon from "@mui/icons-material/Link";
import * as React from "react";

import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { useCopyLink } from "@/shared/hooks/useCopyLink";

interface CopyLinkButtonProps {
  /** URL to copy to clipboard. Must be fully qualified. */
  url: string;
  /** Tooltip text. Defaults to "Copy share link". */
  tooltip?: string;
  /** Size of the icon — small for table rows, medium for headers. */
  size?: "small" | "medium";
  /** Optional click-handler extension — fires after the copy succeeds/fails. */
  onCopied?: (ok: boolean) => void;
}

/**
 * Icon button that copies a URL to the clipboard and confirms with a toast.
 *
 * The copy and both of its toasts come from {@link useCopyLink}, which
 * dispatches into the app-wide notification stack rendered fixed in the
 * bottom-right corner. A local MUI `<Snackbar>` would be wrong here even though
 * it's also `position: fixed`: this button lives inside table cells, and an
 * ancestor with a `transform` (which is how AG Grid positions its rows) becomes
 * the containing block for fixed descendants, so the toast would surface next
 * to the clicked icon instead of in the corner.
 *
 * Distinct from ``CopyToClipboardButton`` because it uses a link icon and a
 * toast (vs. that component's content-copy icon and inline tooltip change). The
 * two visual shapes serve different surfaces: this one is for "share this with a
 * colleague" affordances, typically sat in a table row's action column.
 */
export const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({
  url,
  tooltip = "Copy share link",
  size = "small",
  onCopied,
}) => {
  const copy = useCopyLink(url, onCopied);

  return (
    <RobotoTooltip title={tooltip}>
      <span>
        <RobotoIconButton
          onClick={(e) => void copy(e)}
          size={size}
          eventName="CopyLinkClicked"
          eventProperties={{ link: url }}
        >
          <LinkIcon fontSize="inherit" />
        </RobotoIconButton>
      </span>
    </RobotoTooltip>
  );
};
